import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from "react";

import { useAuth } from "../context/AuthContext";

import {
  listNotifications,
  markNotificationRead,
  markAllNotificationsRead,
} from "../services/notificationService";

const NotificationContext = createContext(null);

/* ===================================================== */
/* PROVIDER                                              */
/* ===================================================== */

export function NotificationProvider({ children }) {
  const { activeCompany } = useAuth(); // ✅ CRITICAL

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  /* ===================================================== */
  /* 📦 Load Notifications                                 */
  /* ===================================================== */

  const refresh = useCallback(async () => {
    if (!activeCompany?._id) return; // ✅ BLOCK until ready

    setLoading(true);
    setError("");

    try {
      const data = await listNotifications();

      const list = Array.isArray(data) ? data : [];

      list.sort(
        (a, b) =>
          new Date(b.createdAt || 0) -
          new Date(a.createdAt || 0)
      );

      setItems(list);
    } catch (e) {
      setError(
        e?.response?.data?.message ||
          e.message ||
          "Failed to load notifications"
      );

      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [activeCompany?._id]);

  /* ===================================================== */
  /* 🔄 Initial Load (SAFE)                                */
  /* ===================================================== */

  useEffect(() => {
    if (!activeCompany?._id) return;

    refresh();
  }, [activeCompany?._id, refresh]);

  /* ===================================================== */
  /* ✅ Mark Single Read (Optimistic)                      */
  /* ===================================================== */

  const markRead = useCallback(async (id) => {
    if (!id) return;

    setItems((prev) =>
      prev.map((n) =>
        String(n._id) === String(id)
          ? { ...n, isRead: true }
          : n
      )
    );

    try {
      await markNotificationRead(id);
    } catch (e) {
      console.error("Mark read failed:", e.message);
    }
  }, []);

  /* ===================================================== */
  /* ✅ Mark All Read                                      */
  /* ===================================================== */

  const markAllRead = useCallback(async () => {
    setItems((prev) =>
      prev.map((n) => ({ ...n, isRead: true }))
    );

    try {
      await markAllNotificationsRead();
    } catch (e) {
      console.error("Mark all read failed:", e.message);
    }
  }, []);

  /* ===================================================== */
  /* ➕ Add Notification (Realtime Safe)                   */
  /* ===================================================== */

  const addNotification = useCallback((notification) => {
    if (!notification?._id) return;

    setItems((prev) => {
      const exists = prev.some(
        (n) => String(n._id) === String(notification._id)
      );

      if (exists) return prev;

      return [
        { ...notification, isRead: false },
        ...prev,
      ];
    });
  }, []);

  /* ===================================================== */
  /* 📊 Derived Values                                     */
  /* ===================================================== */

  const unreadCount = useMemo(() => {
    return items.filter((n) => !n.isRead).length;
  }, [items]);

  /* ===================================================== */
  /* 📦 Context Value                                      */
  /* ===================================================== */

  const value = useMemo(
    () => ({
      items,
      loading,
      error,

      refresh,
      markRead,
      markAllRead,
      addNotification,

      unreadCount,

      setItems,
    }),
    [
      items,
      loading,
      error,
      refresh,
      markRead,
      markAllRead,
      addNotification,
      unreadCount,
    ]
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

/* ===================================================== */
/* HOOK                                                  */
/* ===================================================== */

export function useNotifications() {
  const ctx = useContext(NotificationContext);

  if (!ctx) {
    throw new Error(
      "useNotifications must be used within NotificationProvider"
    );
  }

  return ctx;
}